import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Camera,
  Columns2,
  Eye,
  EyeOff,
  LoaderCircle,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  ShieldCheck,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  type AttentionEngine,
  type AttentionDirection,
  type AttentionSample,
  type AttentionState,
} from "./attention-engine";
import { createMediaPipeAttentionEngine } from "./mediapipe-attention-engine";

export type AttentionPanelMode = "small" | "half" | "large" | "camera-only";

export interface AttentionCameraPanelProps {
  collapsed?: boolean;
  className?: string;
  engine?: AttentionEngine;
  language?: "he" | "en";
  mode?: AttentionPanelMode;
  dailyTotals?: Record<AttentionState, number> | null;
  onModeChange?: (mode: AttentionPanelMode | "hidden") => void;
  onSample?: (sample: AttentionSample) => void;
}

const copy = {
  he: {
    title: "מד ריכוז",
    local: "העיבוד נשאר במכשיר",
    cameraOff: "המצלמה כבויה",
    localOnly: "מקומי בלבד",
    ready: "המדד מוכן להפעלה",
    start: "הפעלת מצלמה",
    stop: "עצירת מדד הריכוז",
    permissionOnClick: "ההרשאה תתבקש רק בלחיצה",
    unsupported: "הדפדפן אינו תומך בגישה למצלמה.",
    permission: "נדרשת הרשאה למצלמה כדי להפעיל את המדד.",
    permissionBlocked:
      "הגישה למצלמה חסומה בהגדרות האתר. יש לאפשר מצלמה עבור focus.aiqel.io ולנסות שוב.",
    unavailable: "לא ניתן להפעיל את המצלמה כרגע.",
    consentTitle: "המצלמה מנתחת איתותי קשב במכשיר בלבד",
    consent:
      "לא נשמרים ולא נשלחים וידאו, תמונות, נקודות פנים או זהות. המדד מעריך נוכחות וכיוון ראש בלבד, ואינו קובע אם עבדת.",
    approve: "אני מבינ/ה, הפעלת מצלמה",
    cancel: "ביטול",
    focused: "מבט לכיוון המסך — משוער",
    away: "נראה שהמבט הוסט או שלא רואים פנים",
    unknown: "לא ניתן לקבוע כרגע",
    calibrating: "כיול קצר — הבט/י למרכז המסך",
    modelUnavailable: "מודל הניתוח לא נטען; לא מוצגת הערכת מבט",
    gaze: {
      center: "מבט משוער: מרכז המסך",
      left: "מבט משוער: שמאלה",
      right: "מבט משוער: ימינה",
      up: "מבט משוער: למעלה",
      down: "מבט משוער: למטה",
      "not-visible": "לא זוהו פנים",
      unknown: "מזהה את כיוון המבט…",
    },
    focusedTime: "קשב משוער",
    awayTime: "מבט מוסח / לא נראה",
    unknownTime: "לא ניתן לקבוע",
    hide: "הסתרת המצלמה",
    small: "מצלמה קטנה",
    half: "חצי מסך",
    large: "מצלמה גדולה",
    cameraOnly: "מצלמה בלבד",
    summaryToday: "סיכום היום",
    summarySession: "סיכום הסשן",
  },
  en: {
    title: "Attention meter",
    local: "Processing stays on this device",
    cameraOff: "Camera is off",
    localOnly: "On-device only",
    ready: "Ready to start",
    start: "Start camera",
    stop: "Stop attention meter",
    permissionOnClick: "Permission is requested only after you click",
    unsupported: "This browser does not support camera access.",
    permission: "Camera permission is required to start the meter.",
    permissionBlocked:
      "Camera access is blocked in this site's settings. Allow camera access for focus.aiqel.io and try again.",
    unavailable: "The camera is not available right now.",
    consentTitle: "Attention signals are analysed only on this device",
    consent:
      "Video, images, face landmarks and identity are never stored or sent. The meter estimates presence and head direction; it does not decide whether you worked.",
    approve: "I understand, start camera",
    cancel: "Cancel",
    focused: "Looking toward the screen — estimated",
    away: "Your gaze may be away or no face is visible",
    unknown: "Unable to determine",
    calibrating: "Short calibration — look at the centre",
    modelUnavailable: "The local model did not load; gaze is not estimated",
    gaze: {
      center: "Looking at the screen centre",
      left: "Looking left",
      right: "Looking right",
      up: "Looking up",
      down: "Looking down",
      "not-visible": "No face visible",
      unknown: "Estimating gaze direction…",
    },
    focusedTime: "Estimated attention",
    awayTime: "Away / not visible",
    unknownTime: "Unknown",
    hide: "Hide camera",
    small: "Small camera",
    half: "Half screen",
    large: "Large camera",
    cameraOnly: "Camera only",
    summaryToday: "Today",
    summarySession: "This session",
  },
} as const;

function formatSeconds(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
}

export function AttentionCameraPanel({
  collapsed = false,
  className,
  engine,
  language = "he",
  mode = "half",
  dailyTotals,
  onModeChange,
  onSample,
}: AttentionCameraPanelProps) {
  const text = copy[language];
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const lastSummaryAtRef = useRef(0);
  const lastSampleAtRef = useRef(0);
  const activeEngine = useMemo(
    () => engine ?? createMediaPipeAttentionEngine(),
    [engine],
  );
  const [isRunning, setIsRunning] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sample, setSample] = useState<AttentionSample | null>(null);
  const [consentPrompt, setConsentPrompt] = useState(false);
  const [totals, setTotals] = useState<Record<AttentionState, number>>({
    focused: 0,
    away: 0,
    unknown: 0,
  });

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    activeEngine.reset();
    setIsReady(false);
    setIsRunning(false);
    lastSampleAtRef.current = 0;
    lastSummaryAtRef.current = 0;
  }, [activeEngine]);

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError(text.unsupported);
      return;
    }
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: "user",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      const startedAt = Date.now();
      lastSampleAtRef.current = startedAt;
      lastSummaryAtRef.current = startedAt;
      setTotals({ focused: 0, away: 0, unknown: 0 });
      setSample(null);
      setIsRunning(true);
      setIsReady(true);
    } catch (caught) {
      let message: string = text.unavailable;
      if (caught instanceof DOMException && caught.name === "NotAllowedError") {
        let permissionState: PermissionState | null = null;
        try {
          permissionState = (
            await navigator.permissions?.query({
              name: "camera" as PermissionName,
            })
          )?.state;
        } catch {
          // Safari does not currently expose the camera permission state.
        }
        message =
          permissionState === "denied"
            ? text.permissionBlocked
            : text.permission;
      }
      setError(message);
      stopCamera();
    }
  }, [stopCamera, text]);

  const requestStart = useCallback(() => {
    if (localStorage.getItem("aiqel-attention-consent-v1") === "accepted") {
      void startCamera();
      return;
    }
    setConsentPrompt(true);
  }, [startCamera]);

  useEffect(() => stopCamera, [stopCamera]);

  useEffect(() => {
    if (!isRunning) return;
    let processing = false;
    const report = async () => {
      if (processing) return;
      processing = true;
      const next = await activeEngine.sample({
        documentVisible: document.visibilityState === "visible",
        video: isReady ? videoRef.current : null,
      });
      setSample(next);
      const priorAt = lastSampleAtRef.current || next.at;
      const elapsedSeconds = Math.min(
        2,
        Math.max(0, Math.round((next.at - priorAt) / 1000)),
      );
      lastSampleAtRef.current = next.at;
      if (elapsedSeconds) {
        setTotals((current) => ({
          ...current,
          [next.state]: current[next.state] + elapsedSeconds,
        }));
      }
      if (next.at - lastSummaryAtRef.current >= 5_000) {
        lastSummaryAtRef.current = next.at;
        onSample?.(next);
      }
      processing = false;
    };
    void report();
    const timer = window.setInterval(() => void report(), 500);
    const onVisibility = () => void report();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [activeEngine, isReady, isRunning, onSample]);

  useEffect(() => {
    if (collapsed) stopCamera();
  }, [collapsed, stopCamera]);

  if (collapsed) return null;

  const status =
    sample?.reason === "calibrating"
      ? text.calibrating
      : sample?.reason === "model-unavailable"
        ? text.modelUnavailable
        : sample
          ? text[sample.state]
          : text.ready;
  const visibleTotals = dailyTotals ?? totals;
  const gazeDirection: AttentionDirection = sample?.direction ?? "unknown";

  return (
    <section
      className={cn(
        "relative isolate min-h-0 overflow-hidden bg-muted text-card-foreground",
        className,
      )}
      aria-labelledby="attention-camera-title"
      dir={language === "he" ? "rtl" : "ltr"}
    >
      <video
        ref={videoRef}
        className={cn(
          "attention-camera-video absolute inset-0 size-full object-cover",
          !isRunning && "hidden",
        )}
        muted
        playsInline
        aria-label={
          language === "he"
            ? "תצוגה מקדימה של המצלמה הקדמית"
            : "Front camera preview"
        }
      />
      {!isRunning && (
        <div className="absolute inset-0 grid place-items-center p-5 text-center text-sm text-muted-foreground">
          <div>
            <Camera className="mx-auto mb-2 size-7" aria-hidden="true" />
            <span>{text.cameraOff}</span>
          </div>
        </div>
      )}

      <header className="attention-camera-toolbar absolute z-20 flex flex-wrap items-center justify-between gap-3 p-3 sm:p-4">
        <div className="flex min-w-0 items-center gap-2 rounded-full bg-background/80 px-3 py-2 shadow-sm backdrop-blur-md">
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
            <Eye className="size-4" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h2 id="attention-camera-title" className="text-sm font-semibold">
              {text.title}
            </h2>
            <p className="truncate text-xs text-muted-foreground">
              {text.local}
            </p>
          </div>
        </div>
        <div
          className="flex items-center gap-0.5 rounded-full bg-background/80 p-1 shadow-sm backdrop-blur-md"
          aria-label="גודל רכיב המצלמה"
        >
          <Button
            variant={mode === "small" ? "secondary" : "ghost"}
            size="icon-sm"
            onClick={() => onModeChange?.("small")}
            aria-label={text.small}
          >
            <Minimize2 />
          </Button>
          <Button
            variant={mode === "half" ? "secondary" : "ghost"}
            size="icon-sm"
            onClick={() => onModeChange?.("half")}
            aria-label={text.half}
          >
            <Columns2 />
          </Button>
          <Button
            variant={mode === "large" ? "secondary" : "ghost"}
            size="icon-sm"
            onClick={() => onModeChange?.("large")}
            aria-label={text.large}
          >
            <Maximize2 />
          </Button>
          <Button
            variant={mode === "camera-only" ? "secondary" : "ghost"}
            size="icon-sm"
            onClick={() => onModeChange?.("camera-only")}
            aria-label={text.cameraOnly}
          >
            <Eye />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => onModeChange?.("hidden")}
            aria-label={text.hide}
          >
            <X />
          </Button>
        </div>
      </header>

      {isRunning && (
        <span className="attention-camera-live absolute z-10 inline-flex items-center gap-1.5 rounded-full bg-background/85 px-2 py-1 text-xs font-medium backdrop-blur-md">
          <span className="size-1.5 rounded-full bg-emerald-500" />
          {text.gaze[gazeDirection]}
        </span>
      )}

      <div className="attention-camera-actions absolute z-20 p-3 sm:p-4">
        <div className="attention-camera-actions-inner flex flex-col gap-3 overflow-y-auto rounded-2xl bg-background/85 p-3 backdrop-blur-md sm:p-4">
          <div className="flex items-center gap-2 text-sm" aria-live="polite">
            {isRunning ? (
              <ShieldCheck
                className="size-4 shrink-0 text-primary"
                aria-hidden="true"
              />
            ) : (
              <EyeOff
                className="size-4 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
            )}
            <span>{status}</span>
          </div>
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
          {isRunning && (
            <section
              aria-label={dailyTotals ? text.summaryToday : text.summarySession}
            >
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                {dailyTotals ? text.summaryToday : text.summarySession}
              </p>
              <dl className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="rounded-xl bg-muted/55 p-2">
                  <dt className="text-muted-foreground">{text.focusedTime}</dt>
                  <dd className="mt-1 font-mono font-semibold" dir="ltr">
                    {formatSeconds(visibleTotals.focused)}
                  </dd>
                </div>
                <div className="rounded-xl bg-muted/55 p-2">
                  <dt className="text-muted-foreground">{text.awayTime}</dt>
                  <dd className="mt-1 font-mono font-semibold" dir="ltr">
                    {formatSeconds(visibleTotals.away)}
                  </dd>
                </div>
                <div className="rounded-xl bg-muted/55 p-2">
                  <dt className="text-muted-foreground">{text.unknownTime}</dt>
                  <dd className="mt-1 font-mono font-semibold" dir="ltr">
                    {formatSeconds(visibleTotals.unknown)}
                  </dd>
                </div>
              </dl>
            </section>
          )}
          {consentPrompt && !isRunning && (
            <div className="rounded-xl bg-muted/70 p-4 text-sm">
              <p className="font-semibold">{text.consentTitle}</p>
              <p className="mt-2 leading-6 text-muted-foreground">
                {text.consent}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => {
                    localStorage.setItem(
                      "aiqel-attention-consent-v1",
                      "accepted",
                    );
                    setConsentPrompt(false);
                    void startCamera();
                  }}
                >
                  <ShieldCheck />
                  {text.approve}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConsentPrompt(false)}
                >
                  {text.cancel}
                </Button>
              </div>
            </div>
          )}
          {!isRunning && !consentPrompt && (
            <p className="text-xs leading-5 text-muted-foreground">
              {text.consent}
            </p>
          )}
          <Button
            className="w-full"
            variant={isRunning ? "outline" : "default"}
            onClick={isRunning ? stopCamera : requestStart}
            aria-pressed={isRunning}
          >
            {isRunning ? (
              <Pause data-icon="inline-start" />
            ) : (
              <Play data-icon="inline-start" />
            )}
            {isRunning ? text.stop : text.start}
          </Button>
          {!isRunning && !error && (
            <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
              <LoaderCircle className="size-3" aria-hidden="true" />
              {text.permissionOnClick}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
